/** ---------------------------------------------------------------------------------------
 * --  LICENSE AGREEMENT -- *
 This software is licensed under the following terms:

 * -- GRANT OF LICENSE | RESTRICTIONS -- *
 Subject to the terms and conditions of this license, the copyright holders
 hereby grant you a non-exclusive, non-transferable, revocable license to use,
 copy, modify, and distribute the software.
 You are expressly prohibited from selling or commercially exploiting the software.
 Unauthorized use, reproduction, or distribution of the software, or any part
 thereof, is strictly prohibited.

 * -- AUTHORIZED USERS | INTELLECTUAL PROPERTY -- *
 This license is granted only to the individual or entity to whom the software is
 directly distributed by the copyright holders. Any use by other individuals or
 entities is unauthorized and in violation of this license. The software is
 protected by copyright and other intellectual property laws.
 The copyright holders retain all rights, title, and interest in the software.

 * -- GOVERNING LAW -- *
 This license is governed by and construed in accordance with the laws of Romania.
 Any legal action or proceeding related to this license shall be brought exclusively
 in the courts of Romania.

 [Weddify x Diaphan Stationery] © [2024] All rights reserved.
 -------------------------------------------------------------------------------------- **/

import React from 'react';
import AppStatic from "@/utils/AppStatic";
import AppFeatures from "@/components/features/AppFeatures";
import AppThemes from "@/components/themes/AppThemes";
import AppButton from "@/components/button/AppButton";
import AppStories from "@/components/stories/AppStories";
import {CiCircleCheck, CiEdit, CiPickerHalf} from "react-icons/ci";
import {BsFileEarmarkExcel} from "react-icons/bs";
import {IoLanguageOutline} from "react-icons/io5";
import {TfiGallery} from "react-icons/tfi";
import {LiaCogsSolid} from "react-icons/lia";
import {RxInput, RxSwitch} from "react-icons/rx";
import {SiWaze} from "react-icons/si";
import {GiLoveSong} from "react-icons/gi";
import {GoShieldCheck} from "react-icons/go";
import {FaEye, FaShoppingBag} from "react-icons/fa";
import {
    PiArrowBendLeftDownLight,
    PiArrowBendUpLeftThin, PiQrCodeThin,
} from "react-icons/pi";
import Image from "next/image";
import weddifySimplifiedNo from "@/assets/images/weddify-simplified-no.webp";
import weddifySimplifiedYes from "@/assets/images/weddify-simplified-yes.webp";


const AppInvite = () => {
    const [switched, setSwitched] = React.useState(false);

    const featureItems = [
        { icon: <CiCircleCheck className="text-5xl m-auto" />, text: AppStatic.featureFiveText.ro.translation },
        { icon: <CiEdit className="text-5xl m-auto" />, text: AppStatic.featureTwoText.ro.translation },
        { icon: <BsFileEarmarkExcel className="text-5xl m-auto" />, text: AppStatic.featureSevenText.ro.translation },
        { icon: <IoLanguageOutline className="text-5xl m-auto" />, text: AppStatic.featureMultilanguageText.ro.translation },
        { icon: <TfiGallery className="text-5xl m-auto" />, text: AppStatic.featureFourText.ro.translation },
        { icon: <CiPickerHalf className="text-5xl m-auto" />, text: AppStatic.featureOneText.ro.translation },
        { icon: <LiaCogsSolid className="text-5xl m-auto" />, text: AppStatic.featureThreeText.ro.translation },
        { icon: <RxInput className="text-5xl m-auto" />, text: AppStatic.featureFiveRSVPText.ro.translation },
        { icon: <SiWaze className="text-5xl m-auto" />, text: AppStatic.featureSixText.ro.translation },
        { icon: <RxSwitch className="text-5xl m-auto" />, text: AppStatic.featureEightText.ro.translation },
        { icon: <GiLoveSong className="text-5xl m-auto" />, text: AppStatic.featureNineText.ro.translation },
        { icon: <PiQrCodeThin className="text-5xl m-auto" />, text: AppStatic.featureTenText.ro.translation },
        { icon: <GoShieldCheck className="text-5xl m-auto" />, text: AppStatic.featureElevenText.ro.translation },
    ];

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setSwitched(!switched);
        }, 5000);

        return () => clearTimeout(timer);
    }, [switched]);

    return (
        <div className="relative">
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
                <div>
                    <div
                        className="mb-2 -mt-8 md:mt-auto flex flex-wrap items-start flex-col-reverse md:items-center md:flex-row justify-start gap-4">
                        <h3 className="md:text-6xl text-4xl text-black font-bold">
                            {AppStatic.appInviteHeading.ro.translation}
                        </h3>
                        <div className="flex gap-4 items-center">
                            <AppStories
                                stories={[
                                    {url: "/clips/stories/weddify-story-how.mp4", type: "video"}
                                ]}
                                storyOpeners={[
                                    {thumbnail: "/clips/stories/weddify-story-how-thumb.webp", label: "Weddify - Why"},
                                ]}
                                extraClass={"mt-0! mb-0!"}
                            />
                            <div className="block md:hidden uppercase font-thin text-sm">
                                <PiArrowBendUpLeftThin/>
                                <p>{AppStatic.openStory.ro.translation}</p>
                            </div>
                        </div>
                    </div>
                    <p className="mb-6 mt-6 font-thin tracking-wider">{AppStatic.inviteFirstProposition.ro.translation}</p>
                    <p className="mb-6 font-thin tracking-wider">{AppStatic.inviteSecondProposition.ro.translation}</p>
                    <p className="mb-10 font-bold">{AppStatic.inviteThirdProposition.ro.translation}</p>
                    <p className="text-3xl md:text-4xl lg:text-6xl mt-4 font-bold">{AppStatic.appHashtagFirst.ro.translation}</p>
                    <p className="text-3xl mb-4 md:text-4xl lg:text-6xl font-bold">{AppStatic.appHashtagSecond.ro.translation}</p>
                    <div className="mb-10 flex flex-wrap gap-2">
                        <AppButton data={{
                            action: "scroll",
                            id: "app-plans",
                            name: AppStatic.mainBuyCta.ro.translation,
                            icon: <FaShoppingBag className="f-2"/>,
                            extraClass: "border-white!"
                        }}/>
                        <AppButton data={{
                            action: "scroll",
                            id: "app-themes",
                            extraClass: "bg-white! text-black! border-black!",
                            blank: true,
                            name: AppStatic.mainThemesCta.ro.translation,
                            icon: <FaEye className="f-2"/>
                        }}/>
                    </div>
                </div>
                <div className="relative flex items-center justify-center mt-4">
                    <div>
                        <Image
                            src={weddifySimplifiedNo}
                            className={`w-full transition-transform duration-500 ${
                                !switched ? 'z-10 scale-100' : 'z-0 scale-90 invisible'
                            }`}
                            alt="Weddify by Diaphan - Online Invitation"
                            loading="lazy"
                        />
                        <Image
                            src={weddifySimplifiedYes}
                            className={`w-full transition-transform duration-500 absolute top-0 left-0 ${
                                switched ? 'z-10 scale-100' : 'z-0 scale-90 invisible'
                            }`}
                            alt="Weddify by Diaphan - Simplified Invitation"
                            loading="lazy"
                        />
                    </div>
                </div>
            </div>
            <AppFeatures featureItems={featureItems} />
            <div id="app-themes" className="pt-24">
                <div
                    className="mb-2 -mt-8 md:mt-auto flex flex-wrap items-start flex-col-reverse md:items-center md:flex-row justify-start gap-4">
                    <h3 className="md:text-6xl text-4xl text-black font-bold">
                        {AppStatic.appInviteThemesHeading.ro.translation}
                    </h3>
                    <div className="flex gap-4 items-center">
                        <AppStories
                            stories={[
                                {url: "/clips/stories/weddify-story-theme.mp4", type: "video"}
                            ]}
                            storyOpeners={[
                                {
                                    thumbnail: "/clips/stories/weddify-story-theme-thumb.webp",
                                    label: "Weddify - Themes"
                                },
                            ]}
                            extraClass={"mt-0! mb-0!"}
                        />
                        <div className="block md:hidden uppercase font-thin text-sm">
                            <PiArrowBendUpLeftThin/>
                            <p>{AppStatic.openStory.ro.translation}</p>
                        </div>
                    </div>
                </div>
                <p className="mb-10 mt-4 font-thin tracking-wider">{AppStatic.appThemesProposition.ro.translation}</p>
                <AppThemes/>
            </div>
            <div className="pt-32 flex text-mono-1000 text-sm gap-2 justify-center items-center">
                <PiArrowBendLeftDownLight/> {AppStatic.appViewWedSnapBlock.ro.translation}
            </div>
        </div>
    );
}

export default AppInvite;
